import React, { useState } from 'react';
import { PlayerActionModal } from './broadcast-min/PlayerActionModal';
import { MinPlayerCard } from './broadcast-min/MinPlayerCard';
import { MinPlayerRow } from './broadcast-min/MinPlayerRow';

// Mock players with varied states
const mockPlayers = [
  {
    id: 1,
    player: { id: 1, first_name: 'Carlos', last_name: 'Fernandez', photo_url: null },
    jersey_number: 10,
    is_captain: true,
    is_starting: true,
    eligibility: { eligible: true, yellowCards: 0, isExpelled: false, isSubstituted: false },
  },
  {
    id: 2,
    player: { id: 2, first_name: 'Miguel', last_name: 'Rodriguez', photo_url: null },
    jersey_number: 7,
    is_captain: false,
    is_starting: true,
    eligibility: { eligible: true, yellowCards: 1, isExpelled: false, isSubstituted: false },
  },
  {
    id: 3,
    player: { id: 3, first_name: 'Juan', last_name: 'Martinez', photo_url: null },
    jersey_number: 9,
    is_captain: false,
    is_starting: true,
    eligibility: { eligible: true, yellowCards: 0, isExpelled: false, isSubstituted: false },
  },
  {
    id: 4,
    player: { id: 4, first_name: 'Pedro', last_name: 'Gonzalez', photo_url: null },
    jersey_number: 4,
    is_captain: false,
    is_starting: true,
    eligibility: { eligible: false, yellowCards: 2, isExpelled: true, isSubstituted: false },
  },
  {
    id: 5,
    player: { id: 5, first_name: 'Luis', last_name: 'Sanchez', photo_url: null },
    jersey_number: 11,
    is_captain: false,
    is_starting: true,
    eligibility: { eligible: false, yellowCards: 0, isExpelled: false, isSubstituted: true },
  },
  {
    id: 6,
    player: { id: 6, first_name: 'Andres', last_name: 'Lopez', photo_url: null },
    jersey_number: 5,
    is_captain: false,
    is_starting: true,
    eligibility: { eligible: true, yellowCards: 0, isExpelled: false, isSubstituted: false },
  },
];

const SectionBox = ({ title, description, children }) => (
  <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
    <div className="p-4 border-b border-gray-700">
      <h2 className="text-lg font-bold text-amber-400">{title}</h2>
      <p className="text-sm text-gray-400 mt-1">{description}</p>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const BroadcastMinTestPage = () => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  const handleAction = (eventType, playerPos) => {
    const name = `${playerPos.player.first_name} ${playerPos.player.last_name}`;
    setLastAction(`${eventType} -> ${name} (#${playerPos.jersey_number})`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-400 mb-2">Broadcast-Min Player Styles</h1>
          <p className="text-gray-400">Compare 3 player layout styles for the iPad operator view</p>
          <p className="text-sm text-gray-500 mt-1">Tap a player to open the action modal (double-action protection)</p>
        </div>

        {/* Last action feedback */}
        {lastAction && (
          <div className="bg-green-900/30 border border-green-600/40 rounded-lg px-4 py-3 text-green-400 text-sm text-center">
            Last action: <strong>{lastAction}</strong>
          </div>
        )}

        {/* Style A: Card Grid */}
        <SectionBox
          title="Style A: Card Grid"
          description="Large photo cards in a grid. Best for big screens or when few players are visible at once."
        >
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {mockPlayers.map((p) => (
              <MinPlayerCard key={p.id} playerPos={p} onSelect={setSelectedPlayer} />
            ))}
          </div>
        </SectionBox>

        {/* Style B: Compact Row */}
        <SectionBox
          title="Style B: Compact Row"
          description="Small avatars in a tight list. Fits the most players on screen. 40px avatars."
        >
          <div className="max-w-md space-y-1">
            {mockPlayers.map((p) => (
              <MinPlayerRow key={p.id} playerPos={p} onSelect={setSelectedPlayer} variant="compact" />
            ))}
          </div>
        </SectionBox>

        {/* Style C: Hybrid Row */}
        <SectionBox
          title="Style C: Hybrid Row"
          description="Medium avatars with comfortable spacing. Good balance for iPad touch. 56px avatars."
        >
          <div className="max-w-lg space-y-1">
            {mockPlayers.map((p) => (
              <MinPlayerRow key={p.id} playerPos={p} onSelect={setSelectedPlayer} variant="hybrid" />
            ))}
          </div>
        </SectionBox>

        {/* Side-by-side comparison */}
        <SectionBox
          title="Side-by-Side: Two Team Columns"
          description="Simulates the actual match layout with Home and Away columns"
        >
          <div className="grid grid-cols-2 gap-4">
            {/* Home */}
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Home Team</div>
              <div className="space-y-1">
                {mockPlayers.slice(0, 3).map((p) => (
                  <MinPlayerRow key={p.id} playerPos={p} onSelect={setSelectedPlayer} variant="compact" />
                ))}
              </div>
            </div>
            {/* Away */}
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Away Team</div>
              <div className="space-y-1">
                {mockPlayers.slice(3).map((p) => (
                  <MinPlayerRow key={p.id} playerPos={p} onSelect={setSelectedPlayer} variant="compact" />
                ))}
              </div>
            </div>
          </div>
        </SectionBox>

        {/* Notes */}
        <div className="bg-gray-800/50 border border-amber-600/30 rounded-lg p-6 text-sm text-gray-400">
          <h3 className="text-amber-400 font-bold mb-2">Notes:</h3>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Style A (Cards):</strong> Best visual, but uses more screen space. Good for 6-8 visible players.</li>
            <li><strong>Style B (Compact):</strong> Most efficient. Fits 11+ players without scrolling on iPad.</li>
            <li><strong>Style C (Hybrid):</strong> Balance between visual appeal and density. Good for iPad landscape.</li>
            <li>All styles use <strong>double-action protection</strong>: tap player, then tap action in modal.</li>
            <li>Inactive players (expelled/substituted) are grayed out and non-tappable.</li>
          </ul>
          <p className="mt-4">Recommended: <strong>Style B (Compact)</strong> for iPad operator use during live matches.</p>
        </div>
      </div>

      {/* Player Action Modal */}
      <PlayerActionModal
        playerPos={selectedPlayer}
        onAction={handleAction}
        onClose={() => setSelectedPlayer(null)}
      />
    </div>
  );
};

export default BroadcastMinTestPage;
